import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Popover,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import { trackAnalyticsEvent } from '../../../../services/analytics';
import {
  DateRangePreset,
  rangeForPreset,
  useAnalyticsFilters,
} from '../hooks/useAnalyticsFilters';

import { Icon } from '@liftango/ui-icons';
interface PresetDef {
  id: DateRangePreset;
  label: string;
}

const PRESET_IDS: DateRangePreset[] = [
  'today',
  'yesterday',
  'last_7d',
  'last_28d',
  'last_90d',
  'this_month',
  'custom',
];

function buildPresets(t: (key: string) => string): PresetDef[] {
  return PRESET_IDS.map((id) => ({ id, label: t(`analytics.dateRange.presets.${id}`) }));
}

function presetLabel(
  filters: { preset?: DateRangePreset; since?: string; until?: string },
  presets: PresetDef[],
  fallback: string,
): string {
  if (filters.preset && filters.preset !== 'custom') {
    return presets.find((p) => p.id === filters.preset)?.label ?? fallback;
  }
  if (filters.since && filters.until) return `${filters.since} → ${filters.until}`;
  return fallback;
}

export default function DateRangePicker() {
  const { t } = useTranslation('admin');
  const PRESETS = React.useMemo(() => buildPresets(t), [t]);
  const defaultLabel = t('analytics.dateRange.buttonDefault');
  const { filters, setFilters } = useAnalyticsFilters();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [draftSince, setDraftSince] = useState(filters.since ?? '');
  const [draftUntil, setDraftUntil] = useState(filters.until ?? '');

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDraftSince(filters.since ?? '');
    setDraftUntil(filters.until ?? '');
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const trackDateRangeApplied = () => {
    trackAnalyticsEvent('admin_analytics_filter_applied', {
      filter_dimension: 'date_range',
      active_view: filters.view,
      timestamp: new Date().toISOString(),
    });
  };

  const applyPreset = (preset: DateRangePreset) => {
    if (preset === 'custom') {
      setFilters({ preset: 'custom' });
      return;
    }
    const range = rangeForPreset(preset);
    if (range) {
      setFilters({ preset, since: range.since, until: range.until });
      trackDateRangeApplied();
    }
    handleClose();
  };

  const applyCustom = () => {
    if (draftSince && draftUntil && draftSince > draftUntil) return;
    setFilters({
      preset: 'custom',
      since: draftSince || null,
      until: draftUntil || null,
    });
    trackDateRangeApplied();
    handleClose();
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<Icon name="calendar" size={20} />}
        onClick={handleOpen}
        sx={{ textTransform: 'none', borderColor: 'divider' }}
      >
        {presetLabel(filters, PRESETS, defaultLabel)}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: { xs: 280, sm: 320 }, maxWidth: '92vw' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('analytics.dateRange.popoverTitle')}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            {PRESETS.map((p) => {
              const selected = (filters.preset ?? 'last_28d') === p.id;
              return (
                <Chip
                  key={p.id}
                  label={p.label}
                  size="small"
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={() => applyPreset(p.id)}
                />
              );
            })}
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
            <TextField
              label={t('analytics.dateRange.from')}
              type="date"
              size="small"
              value={draftSince}
              onChange={(e) => setDraftSince(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            <TextField
              label={t('analytics.dateRange.to')}
              type="date"
              size="small"
              value={draftUntil}
              onChange={(e) => setDraftUntil(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
              error={!!(draftSince && draftUntil && draftSince > draftUntil)}
            />
          </Stack>

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filters.compare}
                onChange={(_, checked) => {
                  trackAnalyticsEvent('admin_analytics_filter_applied', {
                    filter_dimension: 'compare_period',
                    active_view: filters.view,
                    timestamp: new Date().toISOString(),
                  });
                  setFilters({ compare: checked });
                }}
              />
            }
            label={<Typography variant="body2">{t('analytics.dateRange.compare')}</Typography>}
          />

          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button size="small" onClick={handleClose}>
              {t('analytics.dateRange.close')}
            </Button>
            <Button size="small" variant="contained" onClick={applyCustom}>
              {t('analytics.dateRange.apply')}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
